import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  AtendimentoDtoAnotacao,
  AtendimentoView,
  ProcedimentoAtendimentoView,
} from '../../../atendimento/core/_models'
import { useFormik } from 'formik'
import {
  AnexoType,
  AnotacaoType,
  ButtonType,
  ID,
  IconPaths,
  IconSize,
  KTSVG,
  generatePdfUrl,
  showAlert,
  showAlertLoading,
  showConfirmAlert,
  showError,
} from '../../../../../../_metronic/helpers'

import {
  adicionarAnotacao,
  adicionarProcedimento,
  createProcedimentosWithAnexo,
  deleteProcedimento,
  downloadAnexo,
  editarProcedimento,
  getAtendimentoById,
  updateProcedimentosWithAnexo,
} from '../../../atendimento/core/_requests'
import { Spinner } from 'react-bootstrap'
import {
  Button,
  ButtonIcon,
  CheckBox,
  ErrorLabel,
  FormDateInput,
  FormFileInput,
  FormInput,
  InfoAtendimento,
  LoadingIndicator,
  MediaLightbox,
  MediaItem,
  QrCode,
  useModal,
} from '../../../../../components'
import {
  AtendimentoDtoProcedimento,
  AtendimentoDtoProcedimentoUpdate,
  AtendimentoDtoTokenUpload,
} from '../../../atendimento_medicos/atendimentos-medico-list/core/_models'
import {
  generateTokenAnexo,
  readExameIA,
} from '../../../atendimento_medicos/atendimentos-medico-list/core/_requests'
import Swal from 'sweetalert2'
import { examesSchema } from '../validation/examesSchema'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  atendimentoId: ID
  isAtendimento?: boolean
  defineAtendimento?: Function
  isInterconsulta?: boolean
}

const WEB_URL = process.env.REACT_APP_WEB_URL

export const ExamesModal: FC<Props> = ({
  atendimentoId,
  isAtendimento,
  defineAtendimento,
  isInterconsulta,
}) => {
  const { closeModal } = useModal()
  const { t } = useT()
  const [loading, setLoading] = useState(false)
  const [procedimento, setProcedimento] = useState<ProcedimentoAtendimentoView | undefined>(
    undefined
  )
  const [atendimento, setAtendimento] = useState<AtendimentoView>()

  const [isLoading, setIsLoading] = useState(true)
  const [showFileInput, setShowFileInput] = useState(true)
  const [showCarousel, setShowCarousel] = useState(false)
  const [carouselItems, setCarouselItems] = useState<MediaItem[]>([])
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [selectedProcedimentos, setSelectedProcedimentos] = useState<number[]>([])
  const [urlQRCode, setUrlQRCode] = useState<string>('')
  const [lastProcedimentosCount, setLastProcedimentosCount] = useState<number>(0)
  const [anexoUrls, setAnexoUrls] = useState<Map<number, string>>(new Map())

  const procedimentos = useMemo(() => atendimento?.procedimentos ?? [], [atendimento])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      nome: procedimento?.nome || '',
      data: procedimento?.data || '',
      descricao: procedimento?.descricao || '',
      files: [],
    },
    validationSchema: examesSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      setLoading(true)
      try {
        const hasFiles = !!(values.files && values.files.length > 0)
        const isEditing = Boolean(procedimento?.id)

        const common = {
          nome: values.nome,
          data: values.data,
          descricao: values.descricao,
        }

        if (isEditing) {
          const proc: AtendimentoDtoProcedimentoUpdate = {
            ...common,
            id: Number(procedimento!.id),
          }

          if (hasFiles) {
            await updateProcedimentosWithAnexo(Number(atendimentoId), proc, values.files)
          } else {
            await editarProcedimento(Number(atendimentoId), proc)
          }
        } else {
          const proc: AtendimentoDtoProcedimento = {
            ...common,
            id: null,
          }

          if (hasFiles) {
            await createProcedimentosWithAnexo(Number(atendimentoId), proc, values.files)
          } else {
            await adicionarProcedimento(Number(atendimentoId), proc)
          }
        }
        showAlert({
          title: 'Sucesso!',
          text: `Procedimento ${procedimento ? 'editado' : 'adicionado'} com sucesso!`,
          icon: 'success',
        })
      } catch (error) {
        showError(error)
      } finally {
        formik.resetForm()
        setProcedimento(undefined)
        setSubmitting(false)
        setLoading(false)
        setShowFileInput(true)
        getAtendimento()
      }
    },
  })

  const handleSelect = (id: number) => {
    setSelectedProcedimentos((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const handleSelectAll = () => {
    if (selectedProcedimentos.length === procedimentos.length) {
      setSelectedProcedimentos([])
    } else {
      setSelectedProcedimentos(procedimentos.map((p) => p.id))
    }
  }

  const allSelected =
    procedimentos.length > 0 && selectedProcedimentos.length === procedimentos.length

  const editProcedimento = (id: ID) => {
    setShowFileInput(false)
    const procedimentoEdit = atendimento?.procedimentos?.find((p) => p.id === id)

    if (procedimentoEdit) {
      setProcedimento(procedimentoEdit)
      formik.setFieldValue('nome', procedimentoEdit.nome)
      formik.setFieldValue('data', procedimentoEdit.data)
      formik.setFieldValue('descricao', procedimentoEdit.descricao)
      defineAtendimento?.()
    }
  }

  const excluiProcedimento = async (id: ID, anexoId?: number) => {
    try {
      const isConfirmed = await showConfirmAlert()

      if (isConfirmed) {
        await deleteProcedimento(atendimentoId, id)
        setSelectedProcedimentos((prevItems) => prevItems.filter((itemId) => itemId !== id))

        if (anexoId) {
          const filtered = carouselItems.filter((item) => item.id !== anexoId)
          setCarouselItems(filtered)
        }
        showAlert({
          title: 'Sucesso!',
          text: 'Exame excluído com sucesso.',
          icon: 'success',
        })
        getAtendimento()
      }
    } catch (error) {
      showError(error)
      defineAtendimento?.()
    }
  }

  const getAtendimento = useCallback(async () => {
    try {
      const data = await getAtendimentoById(atendimentoId)
      setAtendimento(data)
      setLastProcedimentosCount(data?.procedimentos?.length ?? 0)
    } catch (error) {
      showError(error)
    } finally {
      setIsLoading(false)
    }
  }, [atendimentoId])

  const handleOpenCarousel = async (procedimentoId: number) => {
    if (!atendimento?.procedimentos) return

    const procedimento = atendimento.procedimentos.find((p) => p.id === procedimentoId)
    if (!procedimento?.anexo) return

    const existingItem = carouselItems.find((item) => item.id === procedimento.anexo)
    if (existingItem) {
      const selectedIndex = carouselItems.findIndex((item) => item.id === procedimento.anexo)
      setSelectedIndex(selectedIndex >= 0 ? selectedIndex : 0)
      setShowCarousel(true)
      return
    }

    const cachedUrl = anexoUrls.get(procedimento.anexo)
    if (cachedUrl) {
      window.open(cachedUrl, '_blank')
      return
    }

    showAlertLoading()

    try {
      const loadResults = await Promise.all(
        atendimento.procedimentos
          .filter((p) => p.anexo)
          .map(async (p) => {
            const anexo = await downloadAnexo(atendimento.id, p.anexo)
            return { procedimento: p, anexo }
          })
      )

      const carouselItemsToLoad: MediaItem[] = []
      let selectedAnexoResult: { procedimento: ProcedimentoAtendimentoView; anexo: any } | undefined

      for (const result of loadResults) {
        const { procedimento: procedimentoResult, anexo } = result
        if (procedimentoResult.anexo === procedimento.anexo) selectedAnexoResult = result

        if (anexo.url)
          setAnexoUrls((prev) =>
            new Map(prev).set(Number(procedimentoResult.anexo), String(anexo.url))
          )

        if (!anexo.contentBase64) continue

        const mediaUrl = anexo.type?.includes('pdf')
          ? (generatePdfUrl(anexo.contentBase64, anexo.type) as string)
          : `data:${anexo.type};base64,${anexo.contentBase64}`

        carouselItemsToLoad.push({
          mediaUrl,
          title: anexo.name || 'Arquivo',
          type: anexo.type,
          id: procedimentoResult.anexo,
        })
      }

      if (carouselItemsToLoad.length > 0) setCarouselItems(carouselItemsToLoad)

      if (selectedAnexoResult?.anexo?.contentBase64) {
        const selectedIndex = carouselItemsToLoad.findIndex(
          (item) => item.id === procedimento.anexo
        )
        setSelectedIndex(selectedIndex >= 0 ? selectedIndex : 0)
        setShowCarousel(true)
      } else if (selectedAnexoResult?.anexo?.url) {
        window.open(selectedAnexoResult.anexo.url, '_blank')
      }
      Swal.close()
    } catch (error) {
      showError(error)
    }
  }

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined
    if (urlQRCode) {
      interval = setInterval(async () => {
        const data = await getAtendimentoById(atendimentoId)
        setAtendimento(data)

        const currentCount = data?.procedimentos?.length ?? 0
        if (currentCount > lastProcedimentosCount) {
          setUrlQRCode('')
          if (interval) clearInterval(interval)
        }

        setLastProcedimentosCount(currentCount)
      }, 5000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [atendimentoId, lastProcedimentosCount, urlQRCode])

  useEffect(() => {
    getAtendimento()
  }, [getAtendimento])

  const handleReadExame = async () => {
    const filtered = atendimento?.procedimentos?.filter(
      (procedimento) =>
        selectedProcedimentos.includes(Number(procedimento.id)) && procedimento.anexo
    )
    if (filtered?.length === 0) {
      showAlert({
        title: 'Erro',
        text: 'Exames sem arquivo anexado',
        icon: 'error',
      })
      return
    }
    showAlert({
      title: 'Aguarde...',
      text: 'Transcrevendo os exames...',
      showLoading: true,
      icon: 'info',
    })
    const examesComFalha: string[] = []
    try {
      for (const procedimento of filtered || []) {
        const anexo = await downloadAnexo(Number(atendimento?.id), procedimento.anexo)

        if (anexo.type?.startsWith('video/')) {
          examesComFalha.push(`${procedimento.nome} (vídeo não suportado)`)
          continue
        }

        if (!anexo.type || anexo.type === 'unknown') {
          examesComFalha.push(`${procedimento.nome} (tipo desconhecido)`)
          continue
        }

        const response = await readExameIA(Number(atendimento?.id), anexo)
        if (response.success && response.content) {
          const data: AtendimentoDtoAnotacao = {
            tipo: AnotacaoType.Anamnese,
            anotacao: response.content,
          }
          await adicionarAnotacao(Number(atendimento?.id), data)
        } else {
          examesComFalha.push(procedimento.nome as string)
          continue
        }
      }
      Swal.close()
      showAlert({
        title: 'Sucesso!',
        text: 'Exames transcritos e salvos como Anamnese.',
        icon: 'success',
      })
    } catch (error) {
      showError(error)
    }

    if (examesComFalha.length > 0) {
      const lista = examesComFalha.join(', ')

      showAlert({
        title: 'Aviso',
        html: `
      <p>Os seguintes exames não puderam ser transcritos corretamente:</p>
      <p><strong>${lista}</strong></p>
    `,
        icon: 'warning',
      })
    }
  }

  const handleQRCode = async () => {
    if (!formik.values.nome.trim()) {
      formik.setFieldError('nome', 'O nome do exame é obrigatório para gerar o QR Code.')
      formik.setFieldTouched('nome', true, false)
      return
    }

    const tokenUpload: AtendimentoDtoTokenUpload = {
      tipoAnexo: AnexoType.Procedimento,
      procedimento: formik.values.nome,
    }
    try {
      const { data } = await generateTokenAnexo(Number(atendimentoId), tokenUpload)
      setUrlQRCode(`${WEB_URL}/upload/${data}`)
    } catch (error) {
      showError(error)
    } finally {
      formik.resetForm()
    }
  }

  const handleDownload = async (idAnexo: number) => {
    try {
      const newWindow = window.open('', '_blank')
      const anexo = await downloadAnexo(Number(atendimentoId), idAnexo, true)
      if (anexo?.url) newWindow?.location.replace(anexo.url)
    } catch (error) {
      showError(error)
    }
  }

  return (
    <>
      {isLoading ? (
        <Spinner animation='border' role='status'>
          <span className='visually-hidden'>Carregando...</span>
        </Spinner>
      ) : (
        <>
          {isInterconsulta && atendimento && <InfoAtendimento atendimento={atendimento} />}
          <div
            className='d-flex flex-column justify-content-between'
            style={carouselItems.length > 0 ? { minHeight: 800 } : {}}
          >
            {atendimento?.procedimentos && atendimento?.procedimentos.length > 0 && (
              <div>
                <div className='card-header border-0 pt-5'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold fs-1 text-dark'>Exames Realizados</span>
                  </h3>
                </div>

                <div className='py-5'>
                  {isAtendimento && (
                    <div className='row d-flex align-items-center'>
                      <div className='col-md-3 mb-5'>
                        <CheckBox
                          checked={allSelected}
                          onChange={handleSelectAll}
                          label='Selecionar todos'
                          id='todos'
                        />
                      </div>
                    </div>
                  )}

                  {atendimento.procedimentos.map((procedimento) => (
                    <div className='row d-flex align-items-center mb-3' key={procedimento.id}>
                      <div className='col-md-1 d-flex align-items-center'>
                        {isAtendimento && (
                          <CheckBox
                            checked={selectedProcedimentos.includes(procedimento.id)}
                            onChange={() => handleSelect(procedimento.id)}
                            id={String(procedimento.id)}
                          />
                        )}
                        <KTSVG path={IconPaths.File} size={IconSize.Small} />
                      </div>

                      <div className='col-md-3'>
                        <span className='text-dark fw-bold text-hover-primary mb-1 fs-6'>
                          {procedimento.nome}
                        </span>
                        <span className='text-muted fw-semibold d-block fs-7'>
                          {procedimento.dataFormatada}
                        </span>
                      </div>
                      <div className='col-md-2'>
                        <div className='d-flex flex-column'>
                          <span className='text-bold mb-2 fs-7 fw-semibold'>Descrição</span>

                          <span className='text-muted fs-7 fw-semibold text-break'>
                            {procedimento.descricao || '<Não informado>'}
                          </span>
                        </div>
                      </div>

                      <div className='col-md-4'>
                        <div className='d-flex flex-column'>
                          <span className='text-bold fs-7 fw-semibold'>Arquivo</span>

                          {atendimento.anexos
                            ?.filter((a) => a.id === procedimento.anexo)
                            .map((anexo) => (
                              <Button
                                key={anexo.id}
                                className='btn-light btn-active-light-primary btn-sm w-100'
                                onClick={() => handleDownload(Number(anexo.id))}
                                icon={IconPaths.CloudDownload}
                                title={anexo.descricao}
                              >
                                {anexo.descricao}
                              </Button>
                            ))}
                        </div>
                      </div>
                      <div className='col-md-2 d-flex flex-end'>
                        {procedimento.anexo && (
                          <ButtonIcon
                            icon={IconPaths.Search}
                            onClick={() => handleOpenCarousel(Number(procedimento.id))}
                            iconSize={IconSize.Small}
                            className='p-0'
                          />
                        )}
                        <ButtonIcon
                          icon={IconPaths.Pen}
                          onClick={() => editProcedimento(procedimento.id)}
                          iconSize={IconSize.Small}
                          className='p-0'
                        />
                        <ButtonIcon
                          icon={IconPaths.X}
                          onClick={() => excluiProcedimento(procedimento.id, procedimento.anexo)}
                          iconSize={IconSize.Small}
                          className='p-0'
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {isAtendimento && (
                  <div className='d-flex flex-column flex-end'>
                    {selectedProcedimentos.length === 0 && (
                      <ErrorLabel className='d-flex flex-end'>
                        Selecione pelo menos um exame
                      </ErrorLabel>
                    )}
                    <Button
                      className='btn-primary'
                      size='btn-sm'
                      onClick={handleReadExame}
                      disabled={selectedProcedimentos.length === 0}
                    >
                      Gerar Anamnese a partir dos Exames Selecionados
                    </Button>
                  </div>
                )}
              </div>
            )}
            <form onSubmit={formik.handleSubmit} noValidate>
              <div className='d-flex flex-column me-n7 pe-7'>
                <h4 className='required mb-5'>Dados do Exame</h4>

                <FormInput
                  formik={formik}
                  name='nome'
                  placeholder='Digite o nome do exame'
                  label='Nome'
                  className='mb-3'
                  isRequired
                />
                <FormInput
                  formik={formik}
                  name='descricao'
                  placeholder='Digite a descrição do exame'
                  label='Descrição'
                  className='mb-3'
                />
                <FormDateInput
                  formik={formik}
                  name='data'
                  label='Data'
                  placeholder='Selecione uma data'
                  maxDate={new Date()}
                  className='mb-3'
                  isRequired
                />

                {showFileInput ? (
                  <FormFileInput
                    formik={formik}
                    name='files'
                    className='mb-3'
                    label='Arquivos'
                    isDisabled={formik.isSubmitting}
                    multiple
                  />
                ) : (
                  <div className='d-flex justify-content-end'>
                    <Button
                      className='btn-primary'
                      onClick={() => setShowFileInput(true)}
                      size='btn-sm'
                    >
                      Alterar arquivo
                    </Button>
                  </div>
                )}
              </div>
              {urlQRCode ? (
                <div className='d-flex flex-column justify-content-center align-items-center'>
                  <QrCode className='mt-5' url={urlQRCode} />
                  <div className='text-muted mt-2'>
                    <Spinner size='sm' animation='border' className='me-2' />
                    Aguardando envio do exame...
                  </div>
                </div>
              ) : (
                <div className='d-flex justify-content-end mt-5'>
                  <Button className='btn-primary' size='btn-sm' onClick={handleQRCode}>
                    Gerar QR Code
                  </Button>
                </div>
              )}

              <div className='d-flex justify-content-center mt-5'>
                <Button className='btn-secondary me-2' onClick={closeModal}>
                  Fechar
                </Button>
                <Button
                  type={ButtonType.Submit}
                  className='btn-primary'
                  disabled={loading || formik.isSubmitting}
                >
                  {formik.isSubmitting ? <LoadingIndicator /> : t('common.send')}
                </Button>
              </div>
            </form>
          </div>
        </>
      )}
      <MediaLightbox
        items={carouselItems}
        initialIndex={selectedIndex}
        open={showCarousel}
        onClose={() => setShowCarousel(false)}
      />
    </>
  )
}
